// 2026-05-16 (B2B onboarding fix):
//   BUSINESS user 用の PWA ログイン QR を表示する modal。
//   事業者がスマホで QR を読み取ると enduser PWA `/business-login?u=<username>` が開く。
//
// 表示内容:
//   - QR コード (= 中サイズ)
//   - URL 文字列 + コピー button
//   - 注意書き (= QR には username のみ embed、password は手入力)
//
// Spec: design/coupon-management-redesign-spec.md §B2B onboarding §Pattern A PWA login

import { Button } from '@/components/common/Button';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { URL_CONFIG } from '@/configs/environment';
import { toastify } from '@/utils/toastify';
import { QRCodeSVG } from 'qrcode.react';
import { useMemo } from 'react';

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    username: string | null
}

export default function BusinessLoginQrModal({ open, onOpenChange, username }: Props) {
    const url = useMemo(() => {
        if (!username) return '';
        const base = URL_CONFIG.ENDUSER.url.replace(/\/$/, '');
        return `${base}/business-login?u=${encodeURIComponent(username)}`;
    }, [username]);

    const handleCopy = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            toastify('success', 'URL をコピーしました');
        } catch (_e) {
            toastify('error', 'コピーに失敗しました');
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[480px] sm:max-w-[480px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>ログイン QR</SheetTitle>
                    <SheetDescription>
                        事業者の端末でこの QR を読み取ると、enduser PWA の
                        「事業者ログイン」 画面が開きます。username は自動入力、password は事業者が手入力します。
                    </SheetDescription>
                </SheetHeader>

                <div className='space-y-4 py-6'>
                    {url ?
                        (
                            <>
                                <div className='flex justify-center bg-white p-4 border border-gray-200 rounded'>
                                    <QRCodeSVG value={url} size={256} marginSize={2} />
                                </div>

                                <div>
                                    <label className='block text-sm font-medium mb-1'>username</label>
                                    <p className='font-mono text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200'>
                                        {username}
                                    </p>
                                </div>

                                <div>
                                    <label className='block text-sm font-medium mb-1'>URL</label>
                                    <div className='flex gap-2'>
                                        <input
                                            type='text'
                                            value={url}
                                            readOnly
                                            className='flex-1 px-3 py-2 border border-gray-300 rounded text-xs font-mono'
                                            onClick={e => (e.target as HTMLInputElement).select()}
                                        />
                                        <Button type='button' variant='outline' size='sm' onClick={handleCopy}>
                                            コピー
                                        </Button>
                                    </div>
                                </div>

                                <div className='border border-amber-200 bg-amber-50 rounded p-3 text-xs text-amber-900 space-y-1'>
                                    <p className='font-semibold'>渡し方の注意</p>
                                    <ul className='list-disc pl-4 space-y-0.5'>
                                        <li>QR / URL には **username のみ** が含まれ、password は含まれません</li>
                                        <li>password は別経路 (= LINE / 暗号化 email / 手渡し) で事業者に伝達してください</li>
                                        <li>事業者の端末で QR を読み取り → password 手入力 → ログイン</li>
                                        <li>QR を印刷する場合、共有スペースには貼らないでください (= username 漏洩リスク)</li>
                                    </ul>
                                </div>
                            </>
                        ) :
                        (
                            <p className='text-sm text-gray-500'>username が指定されていません</p>
                        )}
                </div>

                <SheetFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        閉じる
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
